// Серия событий: правило структурой, экземпляры — настоящие строки event (журнал, присутствие,
// позже вопросы). Неподтверждённый экземпляр — зеркало серии: имя, цвет и расписание идут от неё;
// подтверждённый (в том числе сдвинутый или сохранённый из карточки) — свой, серия его не трогает.
import { addDays, daysBetween, fromISO, isoWeekday, longDate, mondayOf, toISO, type ISODate } from '@core/dates';
import { addMonths, getDate, lastDayOfMonth, setDate, startOfMonth } from 'date-fns';
import type { Change, Row } from '@core/store/model';

export type Unit = 'day' | 'week' | 'month' | 'year';
export type RuleEnd = { kind: 'date'; date: ISODate } | { kind: 'count'; count: number };
export type Rule = {
  every: number;
  unit: Unit;
  weekdays?: number[];          // week: ISO 1 (Mon) … 7 (Sun)
  monthly?: 'day' | 'weekday';  // month: то же число | тот же k-й день недели
  end: RuleEnd;
};
export type Span = { start_date: ISODate; end_date: ISODate };
type Event = Row<'event'>;
type Series = Row<'event_series'>;

export const MAX_OCCURRENCES = 100;
export const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ORDINALS = ['first', 'second', 'third', 'fourth', 'last'];

/** Какой по счёту это день недели в месяце: 1–4, пятый считается «последним». */
export const weekdayOrdinal = (s: ISODate): number => Math.min(5, Math.ceil(getDate(fromISO(s)) / 7));
export const ordinalLabel = (n: number): string => ORDINALS[n - 1] ?? 'last';
export const weekdayName = (s: ISODate): string => WEEKDAY_NAMES[isoWeekday(s) - 1]!;

/** k-й (1–4) или последний (5) день недели wd в месяце даты m: четвёртый не позже 28-го, «последний» есть всегда. */
function nthWeekdayOf(m: Date, wd: number, k: number): ISODate {
  if (k === 5) {
    const last = toISO(lastDayOfMonth(m));
    return addDays(last, -((isoWeekday(last) - wd + 7) % 7));
  }
  const first = toISO(startOfMonth(m));
  return addDays(first, (wd - isoWeekday(first) + 7) % 7 + (k - 1) * 7);
}

export const ruleDefault = (start: ISODate): Rule => ({ every: 1, unit: 'week', weekdays: [isoWeekday(start)], monthly: 'day', end: { kind: 'count', count: 4 } });

export function ruleError(rule: Rule): string | null {
  if (!Number.isInteger(rule.every) || rule.every < 1) return 'Repeat every: at least 1';
  if (rule.unit === 'week' && !(rule.weekdays?.length)) return 'Pick at least one weekday';
  if (rule.end.kind === 'count' && (!Number.isInteger(rule.end.count) || rule.end.count < 2)) return 'Repeat at least twice';
  if (rule.end.kind === 'count' && rule.end.count > MAX_OCCURRENCES) return `A series can't have more than ${MAX_OCCURRENCES} events`;
  return null;
}

/**
 * Даты серии: первый экземпляр — сам first (он и задаёт длительность), дальше по правилу строго после него.
 * Конец «после N раз» считает и первый. Не длиннее MAX_OCCURRENCES + 1: планировщик по лишнему видит перебор.
 */
export function occurrences(rule: Rule, first: Span): Span[] {
  const len = daysBetween(first.start_date, first.end_date);
  const limit = rule.end.kind === 'count' ? Math.min(rule.end.count, MAX_OCCURRENCES + 1) : MAX_OCCURRENCES + 1;
  const until = rule.end.kind === 'date' ? rule.end.date : null;
  const out: ISODate[] = [first.start_date];
  const push = (s: ISODate): boolean => {
    if (s <= first.start_date) return true;
    if (until && s > until) return false;
    if (out.length >= limit) return false;
    out.push(s);
    return true;
  };
  const anchor = fromISO(first.start_date);
  if (rule.unit === 'day') {
    for (let k = 1; ; k++) if (!push(addDays(first.start_date, k * rule.every))) break;
  } else if (rule.unit === 'week') {
    const days = [...new Set(rule.weekdays ?? [])].sort((a, b) => a - b);
    const monday = mondayOf(first.start_date);
    outer: for (let w = 0; ; w += rule.every) {
      for (const wd of days) if (!push(addDays(monday, w * 7 + wd - 1))) break outer;
      if (days.length === 0) break; // occurrences без ruleError: пустые дни — пустая серия, не вечный цикл
    }
  } else if (rule.unit === 'month') {
    const wd = isoWeekday(first.start_date);
    const k = weekdayOrdinal(first.start_date);
    for (let m = rule.every; ; m += rule.every) {
      const month = addMonths(startOfMonth(anchor), m);
      const s = rule.monthly === 'weekday' ? nthWeekdayOf(month, wd, k) : toISO(setDate(month, Math.min(getDate(anchor), getDate(lastDayOfMonth(month)))));
      if (!push(s)) break;
    }
  } else {
    for (let y = rule.every; ; y += rule.every) if (!push(toISO(addMonths(anchor, 12 * y)))) break;
  }
  return out.map((s) => ({ start_date: s, end_date: addDays(s, len) }));
}

export function ruleLabel(rule: Rule, first: ISODate): string {
  const n = rule.every;
  const unit = n === 1 ? rule.unit : `${n} ${rule.unit}s`;
  let when = '';
  if (rule.unit === 'week') when = ` on ${[...(rule.weekdays ?? [])].sort((a, b) => a - b).map((d) => WEEKDAY_NAMES[d - 1]).join(', ')}`;
  if (rule.unit === 'month') when = rule.monthly === 'weekday' ? ` on the ${ordinalLabel(weekdayOrdinal(first))} ${weekdayName(first)}` : ` on day ${getDate(fromISO(first))}`;
  const end = rule.end.kind === 'date' ? `until ${longDate(rule.end.date)}` : `${rule.end.count} times`;
  return `every ${unit}${when}, ${end}`;
}

// ---------------------------------------------------------------- что записать

const MIRRORED = ['name', 'sublabel', 'color'] as const;
type Mirrored = Pick<Event, (typeof MIRRORED)[number]>;
const mirror = (e: Mirrored): Mirrored => ({ name: e.name, sublabel: e.sublabel, color: e.color });
const sameMirror = (a: Mirrored, b: Mirrored): boolean => MIRRORED.every((k) => a[k] === b[k]);

/** Экземпляры серии по дате; первый — якорь, от него считается расписание. */
export const membersOf = (seriesId: string, events: Iterable<Event>): Event[] =>
  [...events].filter((e) => e.series_id === seriesId).sort((a, b) => a.start_date.localeCompare(b.start_date) || a.created_at.localeCompare(b.created_at));

const insertOf = (seriesId: string, fields: Mirrored, span: Span): Change =>
  ({ table: 'event', insert: { series_id: seriesId, confirmed: false, ...fields, ...span } });

/**
 * Расписание серии против её экземпляров: подтверждённые на месте (их даты считаются занятыми),
 * неподтверждённые переиспользуются по порядку дат, лишние удаляются, недостающие добавляются.
 * anchor — сохранённое состояние якоря (первого экземпляра); он всегда подтверждён.
 */
function planSchedule(seriesId: string, rule: Rule, anchor: Anchor & { confirmed: true }, others: Event[]): Change[] | { error: string } {
  const spans = occurrences(rule, anchor);
  if (spans.length > MAX_OCCURRENCES) return { error: `A series can't have more than ${MAX_OCCURRENCES} events` };
  const taken = new Set([anchor.start_date, ...others.filter((e) => e.confirmed).map((e) => e.start_date)]);
  const wanted = spans.filter((s) => !taken.has(s.start_date));
  const free = others.filter((e) => !e.confirmed);
  const fields = mirror(anchor);
  const out: Change[] = [];
  wanted.forEach((span, i) => {
    const e = free[i];
    if (!e) { out.push(insertOf(seriesId, fields, span)); return; }
    const set: Record<string, unknown> = {};
    if (e.start_date !== span.start_date || e.end_date !== span.end_date) Object.assign(set, span);
    if (!sameMirror(e, fields)) Object.assign(set, fields);
    if (Object.keys(set).length) out.push({ table: 'event', id: e.id, set, expect_updated_at: e.updated_at });
  });
  for (const e of free.slice(wanted.length)) out.push({ table: 'event', id: e.id, delete: true });
  return out;
}

const OWN = [...MIRRORED, 'start_date', 'end_date'] as const;
/** Поля события из карточки: то, что человек правит руками. */
export type EventFields = Mirrored & Span;
type Anchor = EventFields & Pick<Event, 'id'>;

/**
 * Событие становится серией: anchor — желаемое состояние первого, подтверждённого экземпляра, stored — его
 * сохранённая строка (null — событие новое, вставляется с series_id). Остальные рождаются неподтверждёнными.
 * Одна запись на строку: второй set по той же строке в одной транзакции упёрся бы в expect_updated_at.
 */
export function planSeriesCreate(anchor: Anchor, stored: Event | null, rule: Rule, seriesId: string): Change[] | { error: string } {
  const bad = ruleError(rule);
  if (bad) return { error: bad };
  const rest = planSchedule(seriesId, rule, { ...anchor, confirmed: true }, []);
  if ('error' in rest) return rest;
  const link = { series_id: seriesId, confirmed: true };
  const own: Change = stored
    ? { table: 'event', id: stored.id, set: { ...Object.fromEntries(OWN.filter((k) => stored[k] !== anchor[k]).map((k) => [k, anchor[k]])), ...link }, expect_updated_at: stored.updated_at }
    : { table: 'event', insert: { id: anchor.id, ...Object.fromEntries(OWN.map((k) => [k, anchor[k]])), ...link } };
  return [{ table: 'event_series', insert: { id: seriesId, rule } }, own, ...rest];
}

/**
 * Сохранение экземпляра из карточки: он подтверждается; его имя, цвет и вид уходят в неподтверждённых
 * братьев; новое правило (если менялось) пересчитывает их даты. edited — экземпляр с уже применёнными правками.
 */
export function planMemberSave(series: Series, rule: Rule, edited: Event, members: Event[]): Change[] | { error: string } {
  const bad = ruleError(rule);
  if (bad) return { error: bad };
  const anchor = members[0];
  if (!anchor) return { error: 'Series has no events' };
  const isAnchor = anchor.id === edited.id;
  const anchorNow = isAnchor ? { ...edited, confirmed: true as const } : { ...anchor, ...mirror(edited), confirmed: true as const };
  const others = members.filter((e) => e.id !== anchor.id).map((e) => (e.id === edited.id ? { ...edited, confirmed: true } : e));
  const rest = planSchedule(series.id, rule, anchorNow, others);
  if ('error' in rest) return rest;
  const out: Change[] = [];
  if (JSON.stringify(series.rule) !== JSON.stringify(rule)) out.push({ table: 'event_series', id: series.id, set: { rule }, expect_updated_at: series.updated_at });
  const stored = members.find((e) => e.id === edited.id);
  if (stored) {
    const set: Record<string, unknown> = {};
    for (const k of [...MIRRORED, 'start_date', 'end_date', 'confirmed'] as const) if (stored[k] !== edited[k]) set[k] = edited[k];
    if (!stored.confirmed) set['confirmed'] = true;
    if (Object.keys(set).length) out.push({ table: 'event', id: stored.id, set, expect_updated_at: stored.updated_at });
  }
  if (!isAnchor && !anchor.confirmed && !sameMirror(anchor, edited)) out.push({ table: 'event', id: anchor.id, set: mirror(edited), expect_updated_at: anchor.updated_at });
  return [...out, ...rest];
}

/** Серия уходит; неподтверждённые экземпляры с ней, подтверждённые остаются обычными событиями (series_id обнуляет база). */
export function planSeriesDelete(series: Series, members: Event[]): Change[] {
  return [
    ...members.filter((e) => !e.confirmed).map((e): Change => ({ table: 'event', id: e.id, delete: true })),
    { table: 'event_series', id: series.id, delete: true },
  ];
}

export const planConfirm = (e: Event): Change[] => (e.confirmed ? [] : [{ table: 'event', id: e.id, set: { confirmed: true }, expect_updated_at: e.updated_at }]);

/**
 * Save в карточке события: обычное событие, экземпляр серии (с правилом или без — снятая галочка Repeat
 * удаляет серию, событие остаётся обычным), новая серия из обычного или нового события.
 * id — из черновика (событие могли удалить, пока карточка открыта: тогда live нет, запись без expect).
 */
export function planEventSave(id: string | null, fields: EventFields, live: Event | undefined, series: Series | undefined, members: Event[], rule: Rule | null, newId: () => string): Change[] | { error: string } {
  if (series && live) {
    if (!rule) {
      const own: Change = { table: 'event', id: live.id, set: { ...fields, confirmed: true }, expect_updated_at: live.updated_at };
      return [...planSeriesDelete(series, members.filter((e) => e.id !== live.id)), own];
    }
    return planMemberSave(series, rule, { ...live, ...fields }, members);
  }
  if (rule) return planSeriesCreate({ id: live?.id ?? newId(), ...fields }, live ?? null, rule, newId());
  return id
    ? [{ table: 'event', id, set: fields, ...(live ? { expect_updated_at: live.updated_at } : {}) }]
    : [{ table: 'event', insert: fields }];
}
